/**
 * Gathers the facts a call is read from (callSentence.ts does the reading).
 * Reads only: the Decision row the store already holds, its governance event
 * (the role-at-act stamp, migration 959), the subject (an item, a list, a
 * track's list or a plan), the session it was judged in (the frame), the
 * conditions it rests on, and the subject's relations as they stand now.
 * Anything that cannot be read stays null, and the call shows the blank.
 */
import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { Decision } from '../../types/prioritization/decisions';
import type { CallFacts, CallFrame, CallPlanFrame, CallRelation } from './callSentence';
import { SESSION_RANK_SOURCES } from './rankingDecision';

const DEAD_EDGE_STATUSES = new Set(['retired', 'removed', 'rejected', 'dismissed', 'superseded', 'archived']);

const PLAN_CYCLE_FRAME_COLUMNS = 'name, input_type, input_reference_id, input_snapshot_at, time_horizon, takes_effect_on';

/** The name of what a plan cycle planned from, by the kind the cycle declares. */
async function planInputName(
  client: ReturnType<typeof getTypedSupabaseClient>,
  kind: string | null | undefined,
  id: string | null | undefined,
): Promise<string | null> {
  if (!id) return null;
  const nameOf = async (q: PromiseLike<{ data: { name: string | null } | null }>) => (await q).data?.name?.trim() || null;
  if (kind === 'published_priority_list') return nameOf(client.from('priority_sets').select('name').eq('id', id).maybeSingle());
  if (kind === 'item_pool') return nameOf(client.from('item_pools').select('name').eq('id', id).maybeSingle());
  if (kind === 'collection') return nameOf(client.from('collections').select('name').eq('id', id).maybeSingle());
  return null;
}

function criteriaLabels(config: unknown): string[] {
  const pick = (c: unknown): string | null => {
    if (!c || typeof c !== 'object') return null;
    const o = c as Record<string, unknown>;
    const v = o.label ?? o.name ?? o.key;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  };
  const list: unknown[] = Array.isArray(config)
    ? config
    : config && typeof config === 'object' && Array.isArray((config as Record<string, unknown>).criteria)
      ? ((config as Record<string, unknown>).criteria as unknown[])
      : [];
  return list.map(pick).filter((s): s is string => !!s);
}

export async function fetchCallFacts(
  decision: Decision,
  decisions: readonly Decision[],
  relatedNames: string[] = [],
): Promise<CallFacts> {
  const client = getTypedSupabaseClient();

  const [eventsRes, itemRes, sessionRes, elementsRes, edgesRes] = await Promise.all([
    client
      .from('governance_events')
      .select('actor_role_at_act, actor_right_at_act, event_type, occurred_at, priority_set_id')
      .eq('decision_id', decision.id)
      .order('occurred_at', { ascending: true })
      .limit(5),
    decision.itemId
      ? client.from('items').select('id, title').eq('id', decision.itemId).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    decision.sessionId
      ? client
          .from('prioritization_sessions')
          .select('name, intent, criteria_config, criteria_finalized_at, item_ids')
          .eq('id', decision.sessionId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    client.from('condition_objects').select('kind, statement, status').eq('decision_id', decision.id),
    decision.itemId
      ? client
          .from('item_relationship_edges')
          .select('source_item_id, target_item_id, relationship_type, subtype, status')
          .or(`source_item_id.eq.${decision.itemId},target_item_id.eq.${decision.itemId}`)
      : Promise.resolve({ data: null, error: null }),
  ]);

  const stamped = (eventsRes.data ?? []).find((e) => (e.actor_role_at_act ?? []).length > 0 || e.actor_right_at_act);

  // The subject of an act about a list is the list (the event names it,
  // priority_set_id: a ratification, a publication, a report); the subject of
  // an acceptance is the list the track sent up (migration 1008 names the track
  // in related_objects); the subject of a plan authorization is the plan: the
  // version the act made the plan of record, named by its plan cycle (migration
  // 788 names both in related_objects and sets no item, session or cycle), and
  // its frame is what the plan cycle planned from, its horizon and effect date.
  // Read only when there is no item to be the subject.
  let listName: string | null = null;
  let trackName: string | null = null;
  let planName: string | null = null;
  let planFrame: CallPlanFrame | null = null;
  if (!itemRes.data) {
    const related = (kind: string) => (decision.relatedObjects ?? []).find((o) => o.kind === kind)?.id ?? null;
    const listId = (eventsRes.data ?? []).map((e) => e.priority_set_id).find((id): id is string => !!id) ?? null;
    const trackId = related('track');
    const planVersionId = related('plan_version');
    const planCycleId = related('plan_cycle');
    const [listRes, trackRes, versionRes, cycleRes] = await Promise.all([
      listId ? client.from('priority_sets').select('name').eq('id', listId).maybeSingle() : Promise.resolve({ data: null }),
      trackId ? client.from('tracks').select('name').eq('id', trackId).maybeSingle() : Promise.resolve({ data: null }),
      planVersionId
        ? client.from('plan_versions').select('version_number, plan_cycle_id').eq('id', planVersionId).maybeSingle()
        : Promise.resolve({ data: null }),
      planCycleId ? client.from('plan_cycles').select(PLAN_CYCLE_FRAME_COLUMNS).eq('id', planCycleId).maybeSingle() : Promise.resolve({ data: null }),
    ]);
    listName = listRes.data?.name?.trim() || null;
    trackName = trackRes.data?.name?.trim() || null;
    // A record that names the version but not its cycle: the version knows its cycle.
    const cycleViaVersion =
      !planCycleId && versionRes.data?.plan_cycle_id
        ? await client.from('plan_cycles').select(PLAN_CYCLE_FRAME_COLUMNS).eq('id', versionRes.data.plan_cycle_id).maybeSingle()
        : null;
    const planCycle = cycleRes.data ?? cycleViaVersion?.data ?? null;
    const planCycleName = planCycle?.name?.trim() || null;
    const versionNumber = versionRes.data?.version_number ?? null;
    if (planCycleName) {
      planName = versionNumber === null ? `the plan "${planCycleName}"` : `the plan "${planCycleName}" (version ${versionNumber})`;
    } else if (versionNumber !== null) {
      planName = `plan version ${versionNumber}`;
    }
    // The plan's frame, only when the plan is the subject (a list's publication
    // that also relates to a plan was not judged under the plan's input).
    if (planCycle && planName && !listName && !trackName) {
      planFrame = {
        inputKind: planCycle.input_type ?? null,
        inputName: await planInputName(client, planCycle.input_type, planCycle.input_reference_id),
        inputAsOf: planCycle.input_snapshot_at ?? null,
        horizon: planCycle.time_horizon ?? null,
        takesEffectOn: planCycle.takes_effect_on ?? null,
      };
    }
  }

  let frame: CallFrame | null = null;
  if (sessionRes.data) {
    const s = sessionRes.data;
    frame = {
      sessionName: s.name,
      intent: typeof s.intent === 'string' ? s.intent.replace(/_/g, ' ') : null,
      criteria: criteriaLabels(s.criteria_config),
      poolSize: Array.isArray(s.item_ids) ? s.item_ids.length : null,
      criteriaLockedAt: s.criteria_finalized_at,
    };
  } else if (planFrame) {
    frame = { plan: planFrame };
  }

  // Relations as they stand now, with the other item's title.
  let relations: CallRelation[] | null = null;
  if (decision.itemId) {
    if (edgesRes.error) {
      relations = null;
    } else {
      const edges = (edgesRes.data ?? []).filter((e) => !DEAD_EDGE_STATUSES.has(String(e.status ?? '')));
      const otherIds = Array.from(new Set(edges.map((e) => (e.source_item_id === decision.itemId ? e.target_item_id : e.source_item_id))));
      const names = new Map<string, string>();
      if (otherIds.length) {
        const { data } = await client.from('items').select('id, title').in('id', otherIds);
        (data ?? []).forEach((i) => names.set(i.id, i.title ?? 'an item'));
      }
      relations = edges.map((e) => {
        const out = e.source_item_id === decision.itemId;
        const otherId = out ? e.target_item_id : e.source_item_id;
        return { type: e.relationship_type, subtype: e.subtype, direction: out ? 'out' : 'in', otherName: names.get(otherId) ?? 'an item' };
      });
    }
  }

  const sessionSubject = sessionRes.data?.name
    ? ((decision.source ?? '').includes('result') ? `the result of the session "${sessionRes.data.name}"` : `the session "${sessionRes.data.name}"`)
    : null;

  const byId = new Map(decisions.map((d) => [d.id, d] as const));
  const supersededBy = decisions.find((d) => d.supersedesDecisionId === decision.id);

  return {
    decidedAt: decision.decidedAt,
    decidedByName: decision.decidedByName ?? null,
    roleAtAct: stamped?.actor_role_at_act ?? null,
    rightAtAct: stamped?.actor_right_at_act ?? null,
    source: decision.source ?? null,
    decisionKind: decision.decisionKind,
    decisionClass: decision.decisionClass ?? null,
    title: decision.title,
    // The subject: the item when there is one; else the session when the act
    // was about the session (its publication: a Decision that also relates to
    // the session's track is not about the list the track sent up); else the
    // list, the track's list or the plan the act was about; else the session
    // (a session result is about its session); else what is related.
    subjectName:
      itemRes.data?.title ??
      (sessionSubject && SESSION_RANK_SOURCES.includes(decision.source ?? '')
        ? sessionSubject
        : listName
          ? `the list "${listName}"`
          : trackName
            ? `the list the "${trackName}" track sent up`
            : (planName ?? sessionSubject)),
    subjectKind: decision.subjectKind ?? null,
    relatedNames,
    status: decision.status,
    reviewByDate: decision.reviewByDate ?? null,
    reconsiderationConditions: decision.reconsiderationConditions ?? null,
    frame,
    rationaleReasoning: decision.rationaleReasoning ?? null,
    rationaleContext: decision.rationaleContext ?? null,
    rationaleAlternatives: decision.rationaleAlternatives ?? null,
    body: decision.body ?? null,
    elements: (elementsRes.data ?? []).map((e) => ({ kind: e.kind, statement: e.statement, status: e.status })),
    relations,
    supersedesTitle: decision.supersedesDecisionId ? byId.get(decision.supersedesDecisionId)?.title ?? null : null,
    supersededByTitle: supersededBy?.title ?? null,
    parentTitle: decision.parentDecisionId ? byId.get(decision.parentDecisionId)?.title ?? null : null,
  };
}
